import React from 'react';
import { heroData } from '../../data/mock';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect } from 'react';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = heroData.slides;

  // Auto-advance slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative h-screen min-h-[600px] overflow-hidden">
      {/* Image Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.alt}
            className="w-full h-full object-cover scale-105"
          />
        </div>
      ))}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/60 via-[#0a1628]/40 to-[#0a1628]/70" />

      {/* Content */}
      <div className="relative z-10 h-full flex items-center justify-center px-4 sm:px-8">
        <div className="max-w-[900px] mx-auto text-center">
          <h1
            className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light leading-[1.2] mb-4 sm:mb-6 px-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {heroData.headline}
          </h1>
          
          <p
            className="text-white/80 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 sm:mb-10 font-light leading-relaxed px-4"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {heroData.subheadline}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
            <button
              onClick={() => scrollToSection('#contact')}
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-[11px] sm:text-[12px] font-light tracking-[0.15em] uppercase bg-[#c9a96e] text-[#0a1628] hover:bg-[#d4b87a] transition-all duration-300"
            >
              {heroData.ctaPrimary}
            </button>
            
            <button
              onClick={() => scrollToSection('#services')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-[11px] sm:text-[12px] font-light tracking-[0.15em] uppercase text-white border border-white/30 hover:border-white/60 transition-all duration-300 group"
            >
              {heroData.ctaSecondary}
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>

      {/* Slide Navigation Arrows - Hidden on mobile */}
      <button
        onClick={prevSlide}
        className="hidden sm:flex absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 items-center justify-center text-white/50 hover:text-white transition-colors duration-300"
      >
        <ChevronLeft size={24} strokeWidth={1} />
      </button>
      <button
        onClick={nextSlide}
        className="hidden sm:flex absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 items-center justify-center text-white/50 hover:text-white transition-colors duration-300"
      >
        <ChevronRight size={24} strokeWidth={1} />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-2 sm:gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-8 sm:w-12 h-[2px] transition-all duration-500 ${
              index === currentSlide ? 'bg-[#c9a96e]' : 'bg-white/30 hover:bg-white/50'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;
